// The bounded, TTL-expiring localStorage list that BOTH device-side registries
// are built on (recordingPoisonRegistry, offlineDiffPoisonRegistry) — and the
// third copy this factory exists to prevent. One place for the guarantees:
//
//   - entries carry a numeric `ts` and are dropped on read once older than
//     `ttlMs`, so the list self-heals without a sweeper;
//   - the list is capped at `maxEntries` on write, oldest out first;
//   - every localStorage touch is guarded, so a quota or private-mode failure
//     degrades to "this registry just doesn't persist" — the caller's
//     pre-registry behavior — and never throws into a sync loop or a drain.
//
// Deliberately NOT an entry model: the two registries key and shape their
// entries differently (a (recording, brain) membership mark vs. a per-diff
// attempt counter), and only the storage machinery is common.
export const createBoundedTtlStore = ({ key, ttlMs, maxEntries }) => {
  const load = () => {
    try {
      const raw = localStorage.getItem(key);
      const list = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(list)) return [];
      const cutoff = Date.now() - ttlMs;
      return list.filter((entry) => entry && typeof entry.ts === "number" && entry.ts > cutoff);
    } catch (e) {
      return [];
    }
  };

  const save = (list) => {
    try {
      localStorage.setItem(key, JSON.stringify(list.slice(-maxEntries)));
    } catch (e) {
      // Quota/private-mode failure: nothing persists, so the caller falls back
      // to its pre-registry behavior. Never worse.
    }
  };

  const clear = () => {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      // ignore
    }
  };

  return { load, save, clear };
};
